import React from 'react';
import pawImg from '../assets/paw.png';
import './supplements.css';

const Supplements: React.FC = () => (
  <div className="page-container cream-bg">
    {/* Floating Paw Print Background */}
    <div className="hero-paw-bg">
      <img src={pawImg} className="hero-paw-float paw1" alt="" />
      <img src={pawImg} className="hero-paw-float paw2" alt="" />
      <img src={pawImg} className="hero-paw-float paw3" alt="" />
      <img src={pawImg} className="hero-paw-float paw4" alt="" />
      <img src={pawImg} className="hero-paw-float paw5" alt="" />
      <img src={pawImg} className="hero-paw-float paw6" alt="" />
      <img src={pawImg} className="hero-paw-float paw7" alt="" />
    </div>
    <div className="page-intro">
      <h1 className="page-title">Supplement & Balancing Guide</h1>
      <div className="page-intro-underline" />
      <p className="page-intro-desc">
        Learn about essential supplements for dogs eating homemade diets.
        <br />
        Not all recipes provide every nutrient your dog needs. Discover which supplements may be important, how to use them safely, and when to consult your veterinarian.
      </p>
    </div>
    
    {/* Critical Warning Box */}
    <div style={{
      maxWidth: '800px',
      margin: '2rem auto',
      padding: '1.5rem',
      background: '#fff5f0',
      border: '3px solid #d97706',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(217, 119, 6, 0.15)'
    }}>
      <div style={{ 
        fontSize: '1.5rem', 
        fontWeight: 700, 
        color: '#c14444', 
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span style={{ fontSize: '2rem' }}>⚠️</span>
        Do NOT Combine Balancing Methods
      </div>
      <div style={{ fontSize: '1.1rem', marginBottom: '1rem', fontWeight: 600 }}>
        Choose ONE:
      </div>
      <div style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.75rem', fontSize: '1.05rem' }}>
          <span style={{ color: '#16a34a', fontSize: '1.3rem', marginRight: '0.5rem' }}>✔️</span>
          <strong>Complete vitamin–mineral supplement</strong>
        </div>
        <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '1.1rem', margin: '0.75rem 0' }}>
          OR
        </div>
        <div style={{ marginBottom: '0.75rem', fontSize: '1.05rem' }}>
          <span style={{ color: '#16a34a', fontSize: '1.3rem', marginRight: '0.5rem' }}>✔️</span>
          <strong>Whole food nutrient additions</strong> (eggshell powder, fish oil, kelp, etc.)
        </div>
      </div>
      <div style={{ 
        background: '#fee2e2', 
        padding: '1rem', 
        borderRadius: '8px',
        border: '2px solid #dc2626',
        marginTop: '1rem'
      }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.3rem', marginRight: '0.5rem' }}>❌</span>
          Never use both together
        </div>
        <div style={{ fontSize: '0.95rem', color: '#444' }}>
          This will lead to nutrient imbalances and can harm your dog.
        </div>
      </div>
    </div>

    <div className="supplements-content">
        <h2>Why This Matters</h2>
        <p>
          Homemade dog food can be healthy, fresh, and highly digestible, but it is not automatically complete.
          To meet your dog’s nutritional needs, each recipe must be properly balanced using one complete method.
        </p>
        <ul>
          <li>A complete supplement provides all required nutrients in precise amounts.</li>
          <li>A whole food approach builds those nutrients into the recipe using specific ingredients.</li>
          <li>Both methods work well, but they must be used separately.</li>
        </ul>

        <h2>Important: How Recipes in This App Work</h2>
        <p>
          Every recipe is balanced in one of two ways:
        </p>
        <ul>
          <li>Using a <strong>complete vitamin-mineral supplement</strong></li>
          <strong>OR</strong>
          <li>Using <strong>whole food nutrient sources built directly into the recipe</strong></li>
        </ul>
        <p>
          <b>Do not combine these methods.</b>
        </p>
        <p>
          Adding both will create nutrient excess, especially calcium, iodine, zinc, and fat-soluble vitamins.
        </p>
        <p>Each recipe clearly indicates which balancing method to follow.</p>

        <h2>Whole Food Approach (Encouraged, but Requires Precision)</h2>
        <p>
          If you prefer a whole food approach, that’s absolutely valid, and many dog owners choose it.
        </p> 
        <ul>
          <li>Ingredients like eggshell powder, fish oil, and seeds are <strong>not</strong> optional extras</li>
          <li>They are carefully measured to meet specific nutrient needs</li>
          <li>Adding more “just in case” can unbalance the recipe</li>
        </ul>
        <p>
          Think of these as precise nutrient tools, not general add-ons.
        </p>
        <p>
          If unsure, follow the recipe exactly as written.
        </p>
        
        <h2>Using a Complete Balancer (Simplest &amp; Safest)</h2>
        <p>
          A complete balancer supplement is the easiest way to ensure proper nutrition.
        </p>
        <p>
          It provides nutrients like:
        </p>
        <ul>
          <li>Calcium</li>
          <li>Omega-3 fatty acids</li>
          <li>Vitamins A, D, E</li>
          <li>Zinc and trace minerals</li>
        </ul>
        <div style={{
          background: '#fff3cd',
          color: '#856404',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #ffeeba',
          margin: '1rem 0'
        }}>
          Do not stack multiple supplements unless directed by a veterinarian.
        </div>
    
        <h2>How to Calculate the Correct Amount</h2>
        <p>
            To determine how much supplement to add to a recipe:        
        </p>
        <ol>
            <li>Find your dog’s weight range on the supplement label</li>
            <li>Use the recommended daily amount.</li>
            <li>Multiply that daily amount by the number of days the recipe will last.</li>
            <li>Mix the total evenly into the entire batch.</li>
        </ol>
        <p><strong>Formula:</strong></p>
        <p>
            Daily supplement amount × Number of feeding days = Total amount to add
            <br />
            Meal frequency (once or twice daily) does not change the daily total, only how it is divided per meal.
        </p>


        <h2>If You Modify a Recipe</h2>
        <p>
            Only modify a recipe if:
        </p>
        <ul>
            <li>You understand the nutritional role of the ingredient you are replacing.</li>
            <li>You know how to replace its nutrients with appropriate alternatives.</li>
            <li>You can calculate the correct amounts to maintain balance, <strong>or</strong></li>
            <li>You have consulted a veterinary nutritionist.</li>
        </ul>
        <p>
            Otherwise, follow the recipe exactly as written.
        </p>

        <h2>Tips & Safety</h2>
        <ul>
            <li>Use supplements made for dogs, not humans.</li>
            <li>Follow dosage instructions carefully, more is not better.</li>
            <li>Avoid stacking multiple mineral or vitamin products.</li>
            <li>Monitor your dog’s body condition, stool quality, coat, and energy level.</li>
        </ul>
        <p>For healthy adult dogs, general supplementation guidelines may be appropriate. Dogs with medical conditions or special nutritional needs should be evaluated by a veterinarian before dietary changes are made.</p>
        <h2>Learn More</h2>
        <p>
            For more information, talk to your vet or a veterinary nutritionist. Every dog is different, and supplement needs can vary by age, breed, and health status.
        </p>
        </div>
    </div>
);

export default Supplements;